import {Lang} from 'yougile-lang'
import {ArrUtil, ObjUtil} from 'yougile-util'
import {DataObjectId, Hub, Sandbox, TasksListId} from 'yougile-model'
import {DepartmentsClient} from '@client/departments-client'
import {Storage} from '@client/storage'
import {Sync} from '@client/sync'
import {UserSettings} from '@client/user-settings'
import {crmStore} from '@components/crm/crm-store'
import {
  DefaultListsId,
  FilterValues
} from '@components/my-tasks/tasks-list/tasks-list-store'
import {getAllTasksFromPrivateLists} from '@components/my-tasks/utils'
import {globals} from '@extensions/globals-base'
import {getAllowedMirroredOldestHub} from '@extensions/mirrored-tasks/get-allowed-mirrored-oldest-hub'
import {Sticker} from '@extensions/sticker/sticker'
import {Task} from '@extensions/task'
import {getColumns} from './factories/get-columns'

const DefaultComparators: {
  [key: TasksListId | 'default']: (a: Hub, b: Hub) => number
} = {
  default: (a: Hub, b: Hub) => {
    return b.timestamp - a.timestamp
  },
  'done-tasks': (a: Hub, b: Hub) => {
    return Task.getGTDLastChangeTime(b) - Task.getGTDLastChangeTime(a)
  }
}
export const ElementsOnPage = 7
export const ShowMoreAmount = 30

const isTaskAvailable = (
  box: Sandbox,
  taskHub: Hub,
  withoutDone?: boolean
): boolean => {
  if (box.isDeleted(taskHub.id)) {
    return false
  }

  if (!taskHub || Task.isArchived(taskHub) || Task.isTaskTemplate(taskHub)) {
    return false
  }

  if (withoutDone && Task.isDone(taskHub)) {
    return false
  }

  const oldestMirror = getAllowedMirroredOldestHub(taskHub)
  if (oldestMirror && taskHub.id !== oldestMirror) {
    return false
  }

  const assigned = Sticker.getUsers(taskHub)
  return assigned.includes(globals.currentUserId)
}

export const getAllUserTasksIdsByType = (
  listId: TasksListId | 'private-tasks',
  isPrivateList?: boolean
): DataObjectId[] => {
  const box = Sync.getCurrentSandbox()
  const tasks: Map<DataObjectId, Hub> = new Map()
  let doneTasks: DataObjectId[] = []
  const hierarchy = DepartmentsClient.getHierarchy(box)
  const order = UserSettings.getOrder(listId)
  const allTasksLists = UserSettings.getAllTasksLists()
  const listTasks = UserSettings.getTasksList(listId)?.tasks
  const tasksFromPrivateLists = getAllTasksFromPrivateLists()

  if (DefaultListsId.includes(listId)) {
    Task.forEachTask(box, hierarchy, t => {
      if (isTaskAvailable(box, t)) {
        if (crmStore.isDeal(t)) {
          return
        }

        if (listId === 'private-tasks') {
          tasks.set(t.id, t)
          return
        }

        const taskIsDone = Task.isDone(t)

        if (
          (taskIsDone &&
            listId === 'done-tasks' &&
            !tasksFromPrivateLists.includes(t.id)) ||
          (!taskIsDone && listId === 'current-tasks' && !allTasksLists)
        ) {
          tasks.set(t.id, t)
          return
        }

        if (!taskIsDone && listId === 'current-tasks' && !!allTasksLists) {
          let taskIsInCustomList = false
          for (const tasksListId of ObjUtil.keys(allTasksLists)) {
            const currentList = allTasksLists[tasksListId]
            if (currentList.tasks?.includes(t.id)) {
              taskIsInCustomList = true
              break
            }
          }
          if (!taskIsInCustomList) {
            tasks.set(t.id, t)
          }
        }
      }
    })
  } else {
    if (!listTasks) {
      return []
    }
    listTasks.forEach(taskId => {
      const taskHub = Task.getById(taskId)
      if (taskHub && isTaskAvailable(box, taskHub, !isPrivateList)) {
        if (isPrivateList && Task.isDone(taskHub)) {
          doneTasks.push(taskHub.id)
        } else {
          tasks.set(taskHub.id, taskHub)
        }
      }
    })
  }
  if (doneTasks.length) {
    doneTasks = ArrUtil.defined(
      ArrUtil.unique(doneTasks)
        .map(id => Task.getById(id))
        .sort(DefaultComparators['done-tasks'])
        .map(task => task?.id)
    )
  }

  if (order.length) {
    const nonOrdered: Hub[] = []
    for (const [taskId, task] of tasks) {
      if (!order.includes(taskId)) {
        nonOrdered.push(task)
      }
    }

    return [
      ...nonOrdered.sort((a, b) => b.timestamp - a.timestamp).map(t => t.id),
      ...order.filter(itemId => tasks.has(itemId)),
      ...(doneTasks || [])
    ]
  }

  return [
    ...Array.from(tasks.values())
      .sort(DefaultComparators[listId] || DefaultComparators['default'])
      .map(task => task.id),
    ...(doneTasks || [])
  ]
}

export const FiltersStorageKeys: Record<keyof FilterValues, string> = {
  project: 'tasks-project-filter',
  board: 'tasks-board-filter',
  column: 'tasks-column-filter',
  deadline: 'tasks-deadline-filter',
  author: 'tasks-author-filter',
  stickers: 'tasks-sticker-filter'
}

interface DefaultSettingsFromStorage {
  panelExpanded?: boolean
  activeColumns: Set<string>
  filters: FilterValues
}

export const getDefaultSettingsFromStorage = (
  listId: TasksListId
): DefaultSettingsFromStorage => {
  const companyId = globals.getCompanyId()
  const storageCollapsed = Storage.getItem(
    `${companyId}-collapsed-tasks-panels`
  )
  const panelExpanded =
    !storageCollapsed?.split('.').includes(listId)

  const activeColumns: Set<string> = new Set(
    Storage.getItem(`${companyId}-${listId}-user-tasks-settings`)?.split('.') ??
      getColumns()
        .filter(column => column.isDefault)
        .map(column => column.id)
  )

  const filters: FilterValues = {
    stickers: new Map()
  }

  const filterKeys = Object.keys(
    FiltersStorageKeys
  ) as (keyof typeof FiltersStorageKeys)[]

  filterKeys.forEach(filterStorageKey => {
    const filterStorageValue = Storage.getItem(
      `${companyId}-${listId}-${FiltersStorageKeys[filterStorageKey]}`
    )

    if (filterStorageValue !== undefined) {
      if (filterStorageKey === 'stickers') {
        filters[filterStorageKey] = new Map(
          Object.entries(JSON.parse(filterStorageValue))
        ) as FilterValues['stickers']
        return
      }

      if (filterStorageKey === 'author') {
        filters[filterStorageKey] = filterStorageValue.split(
          '.'
        ) as FilterValues['author']
        return
      }

      if (filterStorageKey === 'deadline') {
        filters[filterStorageKey] = filterStorageValue
          .split('.')
          .map(deadline => new Date(+deadline)) as FilterValues['deadline']

        return
      }

      if (filterStorageKey === 'project') {
        filters[filterStorageKey] = filterStorageValue as DataObjectId | ''
      }

      filters[filterStorageKey] = filterStorageValue as DataObjectId
    }
  })

  return {
    panelExpanded,
    activeColumns,
    filters
  }
}

export const DefaultPrivateTasks = () => [
  {title: Lang.msg.myTasksDefaultPrivateTask1, done: false},
  {title: Lang.msg.myTasksDefaultPrivateTask2, done: false},
  {title: Lang.msg.myTasksDefaultPrivateTask3, done: true},
  {title: Lang.msg.myTasksDefaultPrivateTask4, done: true}
]
